import { FC } from 'react';
import { AttachFile } from '@mui/icons-material';
import { CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import GradientAiIcon from 'components/icons/GradientAiIcon';
import { Column, Row } from 'further-ui/components/Layout';
import { CalendarTime } from 'further-ui/utils';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  NavigationRoute,
  ResponseSegmentType,
} from 'further-types/lp/ai-agent';
import { ChatMessage, PendingInvoice } from '../types';
import EmptyState from './EmptyState';
import FileUploadTaskPrompt from './task-prompts/FileUploadTaskPrompt';
import IncomeExpenseTaskPrompt from './task-prompts/IncomeExpenseTaskPrompt';
import NavigationTaskPrompt from './task-prompts/NavigationTaskPrompt';

const MessagesArea = styled(Column)(({ theme }) => ({
  overflowY: 'auto',
  padding: theme.spacing(5),
  paddingBottom: theme.spacing(2),
  flex: 1,
  minHeight: 0,
  userSelect: 'text',
}));

const UserCard = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(3, 4),
  maxWidth: '88%',
  alignSelf: 'flex-end',
  color: theme.palette.grey[900],
}));

const FileDivider = styled('div')(({ theme }) => ({
  margin: theme.spacing(2, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const AiText = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  wordBreak: 'break-word',
  lineHeight: 1.65,
  paddingLeft: theme.spacing(2),
  color: theme.palette.grey[900],
  '& p': { margin: 0 },
  '& p + p': { marginTop: theme.spacing(3) },
  '& ul, & ol': {
    paddingLeft: theme.spacing(6),
    margin: `${theme.spacing(3)} 0`,
  },
  '& li ul, & li ol': { margin: `${theme.spacing(2)} 0` },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    margin: `${theme.spacing(3)} 0`,
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5, 2),
    textAlign: 'left',
  },
  '& th': { backgroundColor: theme.palette.grey[50], fontWeight: 600 },
}));

const ActionRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(0.25, 0),
}));

const TaskPromptWrapper = styled('div')``;

const Timestamp = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
  fontSize: theme.typography.caption.fontSize,
  marginTop: '-4px',
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
}));

const formatTime = (date: Date) =>
  new CalendarTime(date).toLabel('', 'hh:mm a');

const ALLOWED_MARKDOWN_ELEMENTS = [
  'p',
  'strong',
  'em',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

type Props = {
  messages: Array<ChatMessage>;
  isLoading: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onSend: (text: string) => void;
  onAttachClick: () => void;
  hasInput: boolean;
};

const isIncomeExpenseNav = (
  route: NavigationRoute,
  pendingInvoice: PendingInvoice | null,
) =>
  pendingInvoice !== null &&
  (route === NavigationRoute.IncomeAdd || route === NavigationRoute.ExpenseAdd);

const isEmptyState = (messages: Array<ChatMessage>, isLoading: boolean) =>
  messages.length === 0 && !isLoading;

const MessageList: FC<Props> = ({
  messages,
  isLoading,
  messagesEndRef,
  onSend,
  onAttachClick,
  hasInput,
}) => {
  return (
    <MessagesArea spacing="lg">
      {isEmptyState(messages, isLoading) && (
        <EmptyState onSend={onSend} showPresets={!hasInput} />
      )}

      {messages.map((message, index) => {
        if (message.role === 'user') {
          return (
            <Column key={index} spacing="sm">
              <UserCard>
                <Typography
                  variant="body2"
                  color="grey.900"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {message.content}
                </Typography>
                {message.fileName && (
                  <>
                    <FileDivider />
                    <Row centered spacing="xs">
                      <AttachFile sx={{ fontSize: 16 }} />
                      <Typography variant="body2">
                        {message.fileName}
                      </Typography>
                    </Row>
                  </>
                )}
              </UserCard>
              <Timestamp align="right">
                {formatTime(message.timestamp)}
              </Timestamp>
            </Column>
          );
        }

        if (message.role === 'action') {
          return (
            <ActionRow key={index} justify="space-between" centered>
              <Row centered spacing="xs">
                <GradientAiIcon size={16} />
                <Typography variant="body2" color="text.disabled">
                  {message.content}
                </Typography>
              </Row>
            </ActionRow>
          );
        }

        // assistant: render segments (text and task prompts) in the order Claude returned them
        if (!message.content && !message.segments.length) return null;

        return (
          <Column key={index} spacing="sm">
            {message.segments.map((seg, segIndex) => {
              if (seg.type === ResponseSegmentType.Text) {
                return (
                  <AiText key={segIndex}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      allowedElements={ALLOWED_MARKDOWN_ELEMENTS}
                      unwrapDisallowed
                    >
                      {seg.content}
                    </ReactMarkdown>
                  </AiText>
                );
              }

              if (seg.type === ResponseSegmentType.FileUpload) {
                return (
                  <TaskPromptWrapper key={segIndex}>
                    <FileUploadTaskPrompt
                      label={seg.label}
                      onAttachClick={onAttachClick}
                    />
                  </TaskPromptWrapper>
                );
              }

              if (seg.type === ResponseSegmentType.Navigation) {
                return isIncomeExpenseNav(seg.route, message.pendingInvoice) ? (
                  <TaskPromptWrapper key={segIndex}>
                    <IncomeExpenseTaskPrompt
                      navigation={seg}
                      pendingInvoice={message.pendingInvoice}
                    />
                  </TaskPromptWrapper>
                ) : (
                  <TaskPromptWrapper key={segIndex}>
                    <NavigationTaskPrompt navigation={seg} />
                  </TaskPromptWrapper>
                );
              }
            })}
            <Timestamp>{formatTime(message.timestamp)}</Timestamp>
          </Column>
        );
      })}

      {isLoading && (
        <Row centered spacing="xs" justify="space-between">
          <Row spacing="xs" centered>
            <GradientAiIcon grey />
            <Typography variant="body2" color="grey.500">
              Thinking...
            </Typography>
          </Row>
          <CircularProgress size={16} sx={{ color: 'grey.900' }} />
        </Row>
      )}

      {!isEmptyState(messages, isLoading) && <div ref={messagesEndRef} />}
    </MessagesArea>
  );
};

export default MessageList;
